import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Lock, Search, LogOut } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const STORAGE_KEY = 'paraguana_admin_token';

export const AdminLeadsPage = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Restore session on mount
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setToken(stored);
      fetchLeads(stored, true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeads = async (t, silent = false) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthenticated(false);
        setToken('');
        if (!silent) {
          toast({
            title: language === 'es' ? 'Sesión expirada' : 'Session expired',
            variant: 'destructive',
          });
        }
        return;
      }
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      setLeads(data);
      setAuthenticated(true);
    } catch (err) {
      if (!silent) {
        toast({
          title: language === 'es' ? 'Error al cargar' : 'Failed to load',
          variant: 'destructive',
        });
      }
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.status === 429) {
        toast({
          title: language === 'es' ? 'Demasiados intentos' : 'Too many attempts',
          variant: 'destructive',
        });
        return;
      }
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      sessionStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      await fetchLeads(data.token);
      setPassword('');
      toast({
        title: language === 'es' ? 'Acceso concedido' : 'Access granted',
      });
    } catch (error) {
      toast({
        title: language === 'es' ? 'Contraseña incorrecta' : 'Incorrect password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken('');
    setAuthenticated(false);
    setLeads([]);
    toast({ title: language === 'es' ? 'Sesión cerrada' : 'Signed out' });
  };

  const exportCSV = () => {
    const header = ['Date', 'Name', 'Email', 'Company', 'Country', 'Interest', 'Source', 'Locale', 'Message'];
    const rows = leads.map((l) => [
      new Date(l.created_at).toISOString(),
      l.name,
      l.email,
      l.company,
      l.country,
      l.interest,
      l.source,
      l.locale,
      (l.message || '').replace(/\n/g, ' '),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLeads = leads.filter((lead) =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const interestColors = {
    offtake: 'bg-blue-100 text-blue-800',
    investment: 'bg-emerald-100 text-emerald-800',
    epc: 'bg-purple-100 text-purple-800',
    logistics: 'bg-orange-100 text-orange-800',
    institutional: 'bg-slate-100 text-slate-800',
  };

  if (!authenticated) {
    return (
      <>
        <SEO title="Admin — Leads" path="/admin/leads" />

        <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="p-4 rounded-2xl bg-emerald-50">
                  <Lock className="w-8 h-8 text-emerald-600" />
                </div>
              </div>
              <CardTitle className="text-center font-heading text-2xl">
                {language === 'es' ? 'Acceso Administrativo' : 'Admin Access'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <Label htmlFor="password">
                    {language === 'es' ? 'Contraseña' : 'Password'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  disabled={loading}
                  data-testid="admin-login-btn"
                >
                  {loading
                    ? (language === 'es' ? 'Verificando...' : 'Verifying...')
                    : (language === 'es' ? 'Acceder' : 'Access')
                  }
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title="Admin — Leads Management" path="/admin/leads" />

      <div className="py-12 bg-slate-50 min-h-screen">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 mb-1" data-testid="admin-title">
                {language === 'es' ? 'Gestión de Leads' : 'Leads Management'}
              </h1>
              <p className="text-slate-600">
                {language === 'es'
                  ? `Total de leads: ${leads.length}`
                  : `Total leads: ${leads.length}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={exportCSV}
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="admin-export-csv"
              >
                {language === 'es' ? 'Exportar CSV' : 'Export CSV'}
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Salir' : 'Sign out'}
              </Button>
            </div>
          </div>

          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <Search className="w-5 h-5 text-slate-400" />
                <Input
                  placeholder={language === 'es' ? 'Buscar por nombre, email o empresa...' : 'Search by name, email or company...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                  data-testid="admin-search-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                      <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>{language === 'es' ? 'Empresa' : 'Company'}</TableHead>
                      <TableHead>{language === 'es' ? 'País' : 'Country'}</TableHead>
                      <TableHead>{language === 'es' ? 'Interés' : 'Interest'}</TableHead>
                      <TableHead>{language === 'es' ? 'Origen' : 'Source'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="text-sm">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.company}</TableCell>
                        <TableCell>{lead.country}</TableCell>
                        <TableCell>
                          <Badge className={interestColors[lead.interest] || 'bg-slate-100 text-slate-800'}>
                            {lead.interest}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{lead.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {filteredLeads.length === 0 && (
                  <div className="text-center py-12" data-testid="admin-no-leads">
                    <p className="text-slate-500">
                      {language === 'es' ? 'No se encontraron leads' : 'No leads found'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
